import React, { useEffect, useState, useCallback } from "react";
import { obtenerProductos } from "../services/productoService";
import ProductoForm from "../components/ProductoForm";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";

const parseEmpresa = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const getEmpresaId = () => {
  const a = parseEmpresa(localStorage.getItem("empresaId"));
  if (a) return a;
  try {
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    const user = raw?.user ?? raw ?? null;
    const b = parseEmpresa(user?.empresaId ?? user?.EmpresaId);
    if (b) return b;
  } catch {}
  return null;
};

const ProductosPage = () => {
  const [productos, setProductos] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalPages: 1,
    totalItems: 0,
  });
  const [search, setSearch] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("");
  const empresaId = getEmpresaId();
  const { pageNumber, pageSize } = pagination;

  const cargarProductos = useCallback(async () => {
    try {
      if (!empresaId) return;
      const resp = await obtenerProductos(
        empresaId,
        pageNumber,
        pageSize,
        search,
        estatusFiltro,
      );
      const r = resp?.data ?? {};
      const lista =
        (Array.isArray(r.Data) && r.Data) ||
        (Array.isArray(r.data) && r.data) ||
        [];
      const totItems = r.TotalItems ?? r.totalItems ?? r.Total ?? lista.length;
      const totPages =
        r.TotalPages ??
        r.totalPages ??
        Math.max(1, Math.ceil(totItems / pageSize));
      if (pageNumber > totPages && totPages >= 1) {
        setPagination((prev) => ({ ...prev, pageNumber: totPages }));
        return;
      }
      setProductos(
        lista.map((p) => ({
          id: p.id ?? p.Id,
          ID: p.id ?? p.Id,
          clave: p.clave ?? p.Clave,
          descripcion: p.descripcion ?? p.Descripcion,
          unidadMedida: p.unidadMedida ?? p.UnidadMedida,
          precioUnitario: p.precioUnitario ?? p.precio_Unitario ?? 0,
          estatus: p.estatus ?? p.Estatus,
          claveSAT: p.claveSAT ?? p.ClaveSAT,
          claveUnidad: p.claveUnidad ?? p.ClaveUnidad,
          empresa: p.empresa ?? p.Empresa,
        })),
      );
      setPagination((prev) => ({
        ...prev,
        totalItems: totItems,
        totalPages: totPages,
      }));
    } catch (error) {
      console.error(
        "Error al cargar productos:",
        error?.response?.data ?? error.message,
      );
    }
  }, [empresaId, pageNumber, pageSize, search, estatusFiltro]);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  const columns = [
    { key: "clave", title: "Clave", style: { width: 140 } },
    { key: "descripcion", title: "Descripcion" },
    { key: "unidadMedida", title: "Unidad", style: { width: 100 } },
    {
      key: "precioUnitario",
      title: "Precio",
      style: { width: 120 },
      className: "num",
      render: (r) =>
        `$${Number(r.precioUnitario || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      key: "estatus",
      title: "Estatus",
      style: { width: 100 },
      render: (r) => (
        <span
          className={`lmd-badge ${r.estatus === "A" ? "lmd-badge-success" : "lmd-badge-neutral"}`}
        >
          {r.estatus === "A" ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      key: "acciones",
      title: "",
      style: { width: 90 },
      render: (r) => (
        <button
          className="lmd-btn lmd-btn-warning lmd-btn-sm"
          onClick={() => {
            setProductoSeleccionado(r);
            setIsModalOpen(true);
          }}
        >
          Editar
        </button>
      ),
    },
  ];

  const extraRight = (
    <select
      className="lmd-select"
      value={estatusFiltro}
      onChange={(e) => {
        setEstatusFiltro(e.target.value);
        setPagination((p) => ({ ...p, pageNumber: 1 }));
      }}
    >
      <option value="">Todos los estatus</option>
      <option value="A">Activo</option>
      <option value="I">Inactivo</option>
    </select>
  );

  return (
    <>
      <PageHeader
        title="Productos"
        subtitle="Mantenimiento del catalogo de productos"
      />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por clave o nombre..."
          searchValue={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPagination((p) => ({ ...p, pageNumber: 1 }));
          }}
          onSearch={() => setPagination((p) => ({ ...p, pageNumber: 1 }))}
          sizeValue={pageSize}
          onSizeChange={(n) =>
            setPagination((p) => ({ ...p, pageSize: n, pageNumber: 1 }))
          }
          onAdd={() => {
            setProductoSeleccionado(null);
            setIsModalOpen(true);
          }}
          addText="Nuevo Producto"
          extraRight={extraRight}
        />

        <DataTable columns={columns} rows={productos} keyField="clave" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{pagination.totalItems}</strong> productos
          </span>
          <div className="lmd-pagination-spacer" />
          <button
            className="lmd-page-btn"
            disabled={pageNumber <= 1}
            onClick={() => setPagination((p) => ({ ...p, pageNumber: 1 }))}
          >
            «
          </button>
          <button
            className="lmd-page-btn"
            disabled={pageNumber <= 1}
            onClick={() =>
              setPagination((p) => ({ ...p, pageNumber: p.pageNumber - 1 }))
            }
          >
            ‹
          </button>
          <span
            style={{
              padding: "0 10px",
              fontSize: "var(--fs-sm)",
              color: "var(--clr-text-secondary)",
            }}
          >
            Pag. <strong>{pageNumber}</strong> /{" "}
            <strong>{pagination.totalPages}</strong>
          </span>
          <button
            className="lmd-page-btn"
            disabled={pageNumber >= pagination.totalPages}
            onClick={() =>
              setPagination((p) => ({ ...p, pageNumber: p.pageNumber + 1 }))
            }
          >
            ›
          </button>
          <button
            className="lmd-page-btn"
            disabled={pageNumber >= pagination.totalPages}
            onClick={() =>
              setPagination((p) => ({ ...p, pageNumber: p.totalPages }))
            }
          >
            »
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="lmd-modal-overlay"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="lmd-modal lmd-modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">
                {productoSeleccionado ? "Editar Producto" : "Nuevo Producto"}
              </h3>
              <button
                className="lmd-modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="lmd-modal-body">
              <ProductoForm
                key={productoSeleccionado ? productoSeleccionado.ID : "new"}
                empresaId={empresaId}
                producto={productoSeleccionado || null}
                onSaved={() => {
                  cargarProductos();
                  setIsModalOpen(false);
                }}
                onClose={() => setIsModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductosPage;
